// frontend/src/contexts/DataBoundsContext.jsx
//
// Data coverage windows for the active store: earliest/latest sales month,
// inventory date range, forecast horizon, calendar today + last complete
// month. Consumed by reporting pages that need to gate UI on what data
// actually exists (e.g. month pickers can't go further back than sales
// data, GMROI's default month is `last_complete_month`).
//
// TanStack-backed, with no Provider despite the file name.
// `useDataBounds()` runs a single useQuery.
//
// Shape returned by useDataBounds():
//   {
//     bounds: {
//       sales:     { earliest_month, latest_month },   // 'YYYY-MM' | null
//       inventory: { earliest_date, latest_date },     // 'YYYY-MM-DD' | null
//       forecasts: { earliest_month, latest_month },   // 'YYYY-MM' | null
//       calendar:  { today, last_complete_month },     // 'YYYY-MM-DD' | 'YYYY-MM'
//     } | null,
//     loading, error, refetch
//   }
//
// `staleTime: 5min` — bounds change rarely (a new sale tick only matters
// at month-rollover or after a sync). Tab-back triggers a refresh, picking
// up new bounds after a Shopify sync without a page reload.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { useAuth } from './AuthContext';
import { dataBounds as dataBoundsKey } from '../queries/keys';
import { readResult } from '../queries/api';

const FIVE_MIN = 5 * 60 * 1000;

export function useDataBounds() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: dataBoundsKey.all(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/bounds/data/');
      if (!res.ok) {
        const err = await apiErrorFrom(res, 'GET /api/reports/bounds/data/');
        console.error('DataBoundsContext fetch failed:', err.message);
        throw err;
      }
      return res.json();
    },
    staleTime: FIVE_MIN,
    enabled: !!storeId,
  });

  const { data: bounds, ...result } = readResult(query, t);
  return { bounds, ...result };
}
